<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue';
import { audioManager } from '../../utils/audioManager';
import { utils } from '../../utils';
import { PauseIcon, PlayIcon, ArrowPathIcon } from '@heroicons/vue/16/solid';

const { src, audioKey = 'audio' } = defineProps<{ src: string, audioKey?: string }>()

const isLoading = ref<boolean>(false)
const isPlaying = ref<boolean>(false)
const isDragging = ref<boolean>(false)
const currentTime = ref<number>(0)
const duration = ref<number>(0)

const handleLoading = () => {
    isLoading.value = true
}

const handleLoadMetaData = (audioDuration: number) => {
    duration.value = audioDuration
}

const handlePlaying = () => {
    isLoading.value = false
    isPlaying.value = true
}

const handleTimeUpdate = (audioCurrentTime: number) => {
    if (isDragging.value) return;
    currentTime.value = audioCurrentTime
}

const handleStop = () => {
    isLoading.value = false
    isPlaying.value = false
}

const togglePlay = () => {
    if (isPlaying.value) {
        audioManager.pause(audioKey)
        return
    }

    audioManager.play(src, audioKey, {
        currentTime: currentTime.value,
        priority: 1,
        onloadstart: handleLoading,
        onloadedmetadata: handleLoadMetaData,
        onplaying: handlePlaying,
        ontimeupdate: handleTimeUpdate,
        onpause: handleStop,
        onended: handleStop,
        onerror: handleStop
    })
}

const handleProgressBarChange = (event: Event) => {
    const progressBar = event.target as HTMLInputElement

    // only update audio current time when it's playing to avoid multiple component updating the same audio instance
    if (!isPlaying.value) {
        currentTime.value = Number(progressBar.value)
        return
    }

    audioManager.setTime(audioKey, Number(progressBar.value))
}

const handleDragging = (value: boolean) => {
    isDragging.value = value
}

onBeforeUnmount(() => {
    audioManager.stop(audioKey)
})
</script>

<template>
    <div
        class="flex items-center gap-3 w-full bg-white border border-orange-600 dark:border-fuchsia-500 p-3 rounded-full">
        <!-- Play / Pause Button -->
        <button v-if="isLoading" class="p-2 bg-none rounded-full hover:bg-purple-100">
            <ArrowPathIcon class="size-5 fill-orange-600 dark:fill-fuchsia-500 animate-spin" />
        </button>
        <button v-else @click="togglePlay" class="p-2 bg-none rounded-full hover:bg-purple-100">
            <PlayIcon v-if="!isPlaying" class="size-5 fill-orange-600 dark:fill-fuchsia-500" />
            <PauseIcon v-else class="size-5 fill-orange-600 dark:fill-fuchsia-500" />
        </button>
        <!-- Progress Bar -->
        <input type="range" :min="0" :max="duration" step="0.000001" :value="currentTime"
            class="transistion-all duration-100 w-full h-1 accent-orange-600 dark:accent-fuchsia-500 disabled:accent-fuchsia-500"
            @change="handleProgressBarChange" @mousedown="handleDragging(true)" @mouseup="handleDragging(false)"
            @touchstart="handleDragging(true)" @touchend="handleDragging(false)" :disabled="duration === 0" />
        <span class="text-sm text-orange-600 dark:text-fuchsia-500">
            {{ utils.formatSecondsToMinuteSeconds(Math.floor(currentTime % 60), 'mm:ss') }}
        </span>
    </div>
</template>